/**
 * The story assistant (spec 024): the story wrapper around the shared
 * `ChatPanel` (022). The story editor's graph tab mounts it as its left
 * panel's Assistant tab; the story studio (025) mounts the same component
 * with its selected scene. One conversation per story (`story:<id>`).
 *
 * A turn saves first and posts only the message; its steps are applied with
 * `storyStore.applyOperations` as one entry of the story history (010, 023),
 * so the message's Undo/Redo, the header buttons and Cmd/Ctrl+Z walk the same
 * stack.
 */
import React, { useEffect, useMemo, useState } from 'react';
import type { MissingMediaItem } from '../../api/client';
import {
  ASSISTANT_TEXT, STORY_ASSISTANT_TEXT, STORY_EXAMPLE_PROMPTS, lookedLine, storyKey, useAssistantStore, type AssistantMessage,
} from '../../stores/assistantStore';
import {
  BATCH_NETWORK_NOTICE, BATCH_REDO_FAILED_NOTICE, BATCH_UNDO_CHANGED_NOTICE, useStoryStore, type StoryHistoryEntry,
} from '../../stores/storyStore';
import { ChatAction, ChatPanel, ChatUnavailable, type ChatMessage } from './ChatPanel';
import PicturesNeededCard, { neededGroupsLine } from '../pictures/PicturesNeededCard';
import { useOpenPictures } from '../pictures/usePicturesNav';
import { buildStoryRoute } from '../../utils/storyRoute';
import { storySceneIdOfKey, storySceneKey } from '../../../../Shared/src/storyOperations';
import type { StoryApiResponse } from '../../../../Shared/src/@types';
import { changesOfSteps, groupChanges } from './groupChanges';
import ChangesReport from './ChangesReport';
import type { ChatList } from './bites';

const EMPTY: AssistantMessage[] = [];
/** 023's batch notices (read lazily: tests may mock the store module without them). */
const batchNotices = (): readonly string[] => [BATCH_UNDO_CHANGED_NOTICE, BATCH_REDO_FAILED_NOTICE, BATCH_NETWORK_NOTICE];

export interface StoryAssistantPanelProps {
  storyId: number;
  /** The `StoryScene.id` the author is looking at (the studio's selected scene, 025); sent as `selectedSceneId`. */
  selectedSceneId?: number;
  /** Example prompts for an empty conversation; 024's three by default. */
  examplePrompts?: readonly string[];
  /** Shown under the not-set-up notice (025: the studio's way on without the assistant). */
  unavailableExtra?: React.ReactNode;
  /** Appended to a failed line's Retry (025: "or open Under the hood"). */
  retryExtra?: React.ReactNode;
  /**
   * Spec 028: a Changes card's Open, with the scene's `StoryScene.id` (the
   * studio selects its node, the hood opens its scene tab). Without it, no
   * card offers Open.
   */
  onOpenScene?: (storySceneId: number) => void;
}

/**
 * Spec 028: the Changes bite of an applied turn. With plain changes, the
 * report of cards (title '' since the report draws its header); from an
 * older Service, the summaries as a list; nothing when no step changed
 * anything visible.
 */
export function storyChangesList(
  m: AssistantMessage,
  story: Pick<StoryApiResponse, 'graph' | 'storyScenes'> | null,
  entries: readonly StoryHistoryEntry[],
  onOpenScene?: (storySceneId: number) => void,
): ChatList | null {
  if (!m.changes?.length) return null;
  const plain = m.changeSteps ? changesOfSteps(m.changeSteps) : undefined;
  if (!plain) return { title: 'Changes', items: m.changes, testId: 'assistant-changes' };
  // A scene this turn created: its row, after any redo of the batch.
  const batch = m.turnId !== undefined
    ? entries.find((e): e is Extract<StoryHistoryEntry, { kind: 'batch' }> => e.kind === 'batch' && e.turnId === m.turnId)
    : undefined;
  const created = new Map((batch?.createdStoryScenes ?? m.createdStoryScenes ?? []).map((c) => [c.key, c.storySceneId]));
  const resolve = (key: string) => (created.has(key) ? storySceneKey(created.get(key)!) : key);
  const graph = story?.graph ?? { nodes: [], edges: [], startNodeId: '' };
  const order = story ? buildStoryRoute(graph, story.storyScenes).stops.map((s) => storySceneKey(s.storySceneId)) : [];
  const group = groupChanges(plain.map((c) => (c.scene ? { ...c, scene: resolve(c.scene) } : c)), order);
  if (!group.cards.length) return null;
  const openFor = (key: string) => {
    const id = storySceneIdOfKey(key);
    if (!onOpenScene || id === undefined || !graph.nodes.some((n) => n.type !== 'end' && n.storySceneId === id)) return undefined;
    return () => onOpenScene(id);
  };
  return {
    title: '',
    items: m.changes,
    testId: 'assistant-changes',
    content: <ChangesReport group={group} form="story" openFor={openFor} />,
  };
}

/** The missing-media list: items grouped by scene name in first-seen order, story-wide items last. */
export function missingMediaItems(items: readonly MissingMediaItem[]): string[] {
  const groups = new Map<string, MissingMediaItem[]>();
  for (const item of items) {
    const scene = item.scene ?? '';
    groups.set(scene, [...(groups.get(scene) ?? []), item]);
  }
  const order = [...groups.keys()].filter((k) => k !== '');
  if (groups.has('')) order.push('');
  return order.flatMap((scene) => groups.get(scene)!.map((m) =>
    `${scene || 'Whole story'}: ${m.description} (${m.kind})`));
}

const topTurn = (entries: readonly StoryHistoryEntry[]): number | undefined => {
  const top = entries[entries.length - 1];
  return top && top.kind === 'batch' ? top.turnId : undefined;
};

/**
 * Undo while the turn's batch is the top of `history.past`; Redo while it is
 * the top of `history.future`. A manual edit above it hides both until that
 * edit is undone.
 */
const TurnActions: React.FC<{ message: AssistantMessage; storyId: number }> = ({ message, storyId }) => {
  const pastTop = useStoryStore((s) => topTurn(s.history.past as StoryHistoryEntry[]));
  const futureTop = useStoryStore((s) => topTurn(s.history.future as StoryHistoryEntry[]));
  const pending = useStoryStore((s) => s.batchPending);
  if (message.turnId === undefined) return null;
  const run = async (action: 'undo' | 'redo') => {
    const st = useStoryStore.getState();
    const before = st.notice;
    await st[action]();
    const after = useStoryStore.getState().notice;
    if (after && after !== before && batchNotices().includes(after)) {
      useAssistantStore.getState().pushNotice(storyKey(storyId), after);
    }
  };
  if (pastTop === message.turnId) {
    return <ChatAction onClick={() => { if (!pending) void run('undo'); }} testId="assistant-undo">Undo</ChatAction>;
  }
  if (futureTop === message.turnId) {
    return <ChatAction onClick={() => { if (!pending) void run('redo'); }} testId="assistant-redo">Redo</ChatAction>;
  }
  return null;
};

/** Whole seconds since `running` became true. */
function useElapsedSeconds(running: boolean): number {
  const [start, setStart] = useState<number | null>(null);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!running) { setStart(null); return undefined; }
    setStart(Date.now());
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, [running]);
  return start === null ? 0 : Math.floor((Date.now() - start) / 1000);
}

export const StoryAssistantPanel: React.FC<StoryAssistantPanelProps> = ({
  storyId, selectedSceneId, examplePrompts = STORY_EXAMPLE_PROMPTS, unavailableExtra, retryExtra, onOpenScene,
}) => {
  const key = storyKey(storyId);
  const status = useAssistantStore((s) => s.status);
  const loadStatus = useAssistantStore((s) => s.loadStatus);
  const messages = useAssistantStore((s) => s.conversations[key] ?? EMPTY);
  const running = useAssistantStore((s) => !!s.running[key]);
  const sendStory = useAssistantStore((s) => s.sendStory);
  const retryStory = useAssistantStore((s) => s.retryStory);
  const stop = useAssistantStore((s) => s.stop);
  const previewRunning = useStoryStore((s) => s.previewRunnerActive);
  const hasScenes = useStoryStore((s) => (s.story?.storyScenes.length ?? 0) > 0);
  const elapsed = useElapsedSeconds(running);
  const openPictures = useOpenPictures();
  const story = useStoryStore((s) => s.story);
  const past = useStoryStore((s) => s.history.past as StoryHistoryEntry[]);
  const future = useStoryStore((s) => s.history.future as StoryHistoryEntry[]);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  const opts = selectedSceneId !== undefined ? { selectedSceneId } : undefined;
  const chatMessages = useMemo(() => messages.map((m): ChatMessage => {
    const lists: ChatList[] = [];
    const changes = storyChangesList(m, story, [...past, ...future], onOpenScene);
    if (changes) lists.push(changes);
    // Spec 026: the Pictures needed card, when the turn left pictures to fill.
    if (m.pictures && m.pictures.needed.length > 0) {
      lists.push({
        title: 'Pictures needed',
        items: [neededGroupsLine(m.pictures.needed)],
        testId: 'assistant-pictures-card',
        content: <PicturesNeededCard pictures={m.pictures} onOpen={() => openPictures({ filter: 'needed' })} />,
      });
    }
    if (m.missingMedia?.length) lists.push({ title: 'Missing media', items: missingMediaItems(m.missingMedia), testId: 'assistant-missing-media' });
    return {
      id: m.id,
      role: m.role,
      text: m.text,
      ...(lists.length ? { lists } : {}),
      ...(m.retry ? { retry: true } : {}),
      ...(m.views?.length ? { looked: lookedLine(m.views, true) } : {}),
    };
  }), [messages, openPictures, story, past, future, onOpenScene]);
  const byId = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);
  const lastId = messages[messages.length - 1]?.id;

  if (status === 'unavailable') {
    return (
      <>
        <ChatUnavailable text={ASSISTANT_TEXT.unavailable} testId="assistant-unavailable" />
        {unavailableExtra}
      </>
    );
  }

  return (
    <ChatPanel
      key={key}
      conversationKey={key}
      messages={chatMessages}
      running={running}
      send={(text) => { void sendStory(storyId, text, opts); }}
      stop={() => stop(key)}
      retry={(id) => { void retryStory(storyId, Number(id), opts); }}
      examplePrompts={examplePrompts}
      emptyHint="Describe the training, or what to change in this story."
      disabled={previewRunning || status !== 'available'}
      notice={previewRunning ? { text: STORY_ASSISTANT_TEXT.preview, testId: 'assistant-preview-notice' } : undefined}
      placeholder="e.g. Add a scene where the customer escalates"
      thinkingText={`${STORY_ASSISTANT_TEXT.building} ${elapsed} s`}
      retryExtra={retryExtra}
      renderActions={(m) => {
        const message = byId.get(Number(m.id));
        if (!message) return null;
        const showBuild = message.buildNow && !hasScenes && message.id === lastId && !running;
        return (
          <>
            <TurnActions message={message} storyId={storyId} />
            {showBuild && (
              <ChatAction onClick={() => { void sendStory(storyId, STORY_ASSISTANT_TEXT.buildNow, opts); }} testId="assistant-build-now">
                Build it now
              </ChatAction>
            )}
          </>
        );
      }}
    />
  );
};

export default StoryAssistantPanel;
